import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { driverAPI, financeAPI } from '../api/api';
import { useAuth } from '../App';
import DriverStatusBadge, { DeliveryStatusBadge } from '../components/DriverStatusBadge';
import RatingModal from '../components/RatingModal';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function PreferredDriverPanel() {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const codeRes = await driverAPI.getMyCode();
      setCode(codeRes.data.driverCode || '');
    } catch (e) { /* ignore */ }
    try {
      const reqRes = await driverAPI.getPreferredRequests();
      setPending(reqRes.data.pending || []);
      setActive(reqRes.data.active || []);
    } catch (e) { /* ignore */ }
  }

  useEffect(() => { load(); }, []);

  async function respond(restaurantId, action) {
    try {
      await driverAPI.respondToPreferredRequest(restaurantId, action);
      load();
    } catch (e) { /* ignore */ }
  }

  function copyCode() {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
      <h2 className="font-semibold mb-3 flex items-center gap-2"><span>⭐</span> Preferred Driver</h2>
      <p className="text-gray-400 text-xs mb-3">
        Share your code with restaurants. When they add it, you'll get their deliveries first.
      </p>
      {code && (
        <div className="flex items-center gap-2 mb-4">
          <code className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-yellow-400 font-mono text-sm tracking-wider">{code}</code>
          <button onClick={copyCode}
            className="px-4 py-2.5 bg-gray-800 border border-gray-700 hover:border-yellow-400 rounded-xl text-sm text-white transition">
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Requests</p>
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.restaurantId} className="flex items-center justify-between bg-gray-800/50 rounded-xl px-4 py-3">
                <span className="text-white text-sm font-medium">{r.name}</span>
                <div className="flex gap-2">
                  <button onClick={() => respond(r.restaurantId, 'accept')}
                    className="px-3 py-1.5 bg-green-500 hover:bg-green-400 text-white text-xs font-bold rounded-lg transition">
                    Accept
                  </button>
                  <button onClick={() => respond(r.restaurantId, 'decline')}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-bold rounded-lg transition">
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Preferred for</p>
          <div className="flex flex-wrap gap-2">
            {active.map((r) => (
              <span key={r.restaurantId} className="text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400">{r.name}</span>
            ))}
          </div>
        </div>
      )}

      {pending.length === 0 && active.length === 0 && (
        <p className="text-gray-600 text-xs">No restaurant requests yet.</p>
      )}
    </div>
  );
}

export default function DriverPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [driver, setDriver] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [locationWatching, setLocationWatching] = useState(false);
  const [wakeUnavailable, setWakeUnavailable] = useState(false);

  const [ratingDelivery, setRatingDelivery] = useState(null);
  const socketRef = useRef(null);
  const watchIdRef = useRef(null);
  const activeJobRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Load driver profile + jobs
  useEffect(() => {
    async function load() {
      try {
        const [dRes, jRes] = await Promise.all([
          driverAPI.getMe(),
          driverAPI.getMyJobs(),
        ]);
        setDriver(dRes.data);
        // Pull computed earnings (single source of truth) for the display tiles.
        try {
          const eRes = await financeAPI.getDriverEarnings(dRes.data._id);
          setEarnings(eRes.data);
        } catch (e) { /* tiles fall back to 0.00 until this loads */ }
        const jobs = Array.isArray(jRes.data) ? jRes.data : [];
        setJobs(jobs);
        const active = jobs.find((j) =>
          ['assigned', 'accepted', 'picked_up'].includes(j.status)
        );
        setActiveJob(active || null);
      } catch (err) {
        console.error('Driver panel load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Poll for new jobs every 30 seconds as fallback
  useEffect(() => {
    if (!driver) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await driverAPI.getMyJobs();
        const jobs = Array.isArray(data) ? data : [];
        setJobs(jobs);
        const active = jobs.find(j => ['assigned','accepted','picked_up'].includes(j.status));
        setActiveJob(active || null);
      } catch (err) {}
    }, 15000);
    return () => clearInterval(interval);
  }, [driver]);

  // Socket.IO connection
  useEffect(() => {
    if (!driver) return;

    socketRef.current = io(SOCKET_URL, { transports: ['websocket', 'polling'] });

    socketRef.current.on('connect', () => {
      socketRef.current.emit('join:driver', { driverId: driver._id });
    });

    // New job assigned
    socketRef.current.on('job:assigned', ({ delivery }) => {
      setJobs((prev) => [delivery, ...prev]);
      setActiveJob(delivery);
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [driver]);

  // Keep the active job in a ref so the geolocation watch callback always
  // sees the current delivery id (the callback closes over state otherwise).
  useEffect(() => {
    activeJobRef.current = activeJob;
  }, [activeJob]);

  // Auto-resume GPS sharing after a load/refresh if the driver is mid-job.
  // watchPosition and the locationWatching flag don't survive a page reload, so
  // without this a driver who refreshes goes silently dark to the restaurant.
  // Idempotent via the guard in startLocationTracking.
  useEffect(() => {
    if (driver && activeJob) startLocationTracking();
  }, [driver, activeJob]);

  // Keep the screen awake the whole time the panel is open, so a driver's phone
  // doesn't auto-lock mid-delivery and suspend GPS. The OS releases the lock
  // whenever the page is hidden, so we re-acquire on visibility change. This
  // only holds while the app is foregrounded — it can't keep a backgrounded or
  // locked phone tracking; that needs a native wrapper.
  useEffect(() => {
    async function acquire() {
      if (!('wakeLock' in navigator)) { setWakeUnavailable(true); return; }
      if (document.visibilityState !== 'visible') return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeUnavailable(false);
        wakeLockRef.current.addEventListener?.('release', () => {
          wakeLockRef.current = null;
        });
      } catch (e) {
        setWakeUnavailable(true);
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) acquire();
    }
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch (e) {}
        wakeLockRef.current = null;
      }
    };
  }, []);

  // GPS location tracking
  function startLocationTracking() {
    if (!navigator.geolocation) {
      alert('Geolocation not supported on this device');
      return;
    }
    if (watchIdRef.current !== null) return; // already sharing — don't double-subscribe

    setLocationWatching(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          await driverAPI.updateLocation(latitude, longitude);
          // Also emit via socket for real-time admin view
          socketRef.current?.emit('driver:updateLocation', {
            driverId: driver._id,
            deliveryId: activeJobRef.current?._id,
            coordinates: [longitude, latitude],
          });
        } catch (err) {
          console.warn('Location update failed:', err.message);
        }
      },
      (err) => console.warn('GPS error:', err.message),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
  }

  function stopLocationTracking() {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setLocationWatching(false);
  }

  useEffect(() => {
    return () => stopLocationTracking();
  }, []);

  async function handleStatusChange(newStatus) {
    setStatusLoading(true);
    try {
      const { data } = await driverAPI.updateStatus(newStatus);
      setDriver(data);
      if (newStatus === 'available') startLocationTracking();
      else if (newStatus === 'offline') stopLocationTracking();
    } catch (err) {
      console.error('Status change failed:', err);
    } finally {
      setStatusLoading(false);
    }
  }

  async function handleJobStatusUpdate(jobId, newStatus) {
    try {
      const { data: updated } = await driverAPI.getMe(); // refresh
      const { deliveryAPI } = await import('../api/api');
      await deliveryAPI.updateStatus(jobId, newStatus);

      // Emit socket event
      socketRef.current?.emit(`job:${newStatus === 'delivered' ? 'delivered' : newStatus}`, {
        jobId,
        driverId: driver._id,
      });

      // Refresh jobs
      const jRes = await driverAPI.getMyJobs();
      setJobs(jRes.data);
      const active = jRes.data.find((j) =>
        ['assigned', 'accepted', 'picked_up'].includes(j.status)
      );
      setActiveJob(active || null);

      // Turnkey: accepting (or being mid-delivery) auto-starts GPS sharing so the
      // restaurant can track the driver without them remembering to go Available
      // first. Idempotent — a no-op if already sharing.
      if (['accepted', 'picked_up'].includes(newStatus)) {
        startLocationTracking();
      }

      if (newStatus === 'delivered') {
        const completedJob = activeJob;
        handleStatusChange('available');
        setTimeout(() => setRatingDelivery(completedJob), 800);
      }
    } catch (err) {
      console.error('Job update error:', err);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 md:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">{driver?.name}</h1>
          <p className="text-xs text-gray-500">Driver Panel</p>
        </div>
        <div className="flex items-center gap-3">
          {driver && <DriverStatusBadge status={driver.status} />}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="text-gray-400 hover:text-white text-sm transition"
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="px-4 md:px-8 py-6 max-w-2xl mx-auto space-y-6">
        {/* Status controls */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <h2 className="font-semibold mb-4">Your Status</h2>
          <div className="flex gap-2 flex-wrap">
            {['available', 'busy', 'offline'].map((s) => (
              <button
                key={s}
                onClick={() => handleStatusChange(s)}
                disabled={statusLoading || driver?.status === s}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium capitalize transition-all
                  ${driver?.status === s
                    ? s === 'available' ? 'bg-green-500 text-white'
                    : s === 'busy' ? 'bg-yellow-400 text-black'
                    : 'bg-gray-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }
                  disabled:opacity-50
                `}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Location tracking toggle */}
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-300">GPS Location Sharing</p>
              <p className="text-xs text-gray-500 mt-0.5">Required to receive nearby jobs</p>
            </div>
            <button
              onClick={locationWatching ? stopLocationTracking : startLocationTracking}
              className={`
                relative w-12 h-6 rounded-full transition-colors
                ${locationWatching ? 'bg-green-500' : 'bg-gray-700'}
              `}
            >
              <span
                className={`
                  absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform
                  ${locationWatching ? 'translate-x-6' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          {activeJob && wakeUnavailable && (
            <p className="mt-3 text-xs text-yellow-400/80">
              ⚠️ Keep the app open and your screen on during this delivery — your phone may lock and pause location sharing.
            </p>
          )}
        </div>

        {/* Earnings summary */}
        {!driver?.stripeAccountId && (
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-2xl p-4 mb-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-yellow-400 font-semibold text-sm">Set Up Payouts</p>
              <p className="text-gray-400 text-xs mt-0.5">Connect your bank to receive earnings directly</p>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/finance/drivers/${driver._id}/onboard`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${localStorage.getItem('starfuel_token')}` },
                  });
                  const data = await res.json();
                  if (data.url) window.open(data.url, '_blank');
                  else alert('Contact legal@starfuelcarfleet.com to set up payouts');
                } catch (err) { alert('Contact legal@starfuelcarfleet.com to set up payouts'); }
              }}
              className="px-4 py-2 bg-yellow-400 text-black text-sm font-bold rounded-xl hover:bg-yellow-300 transition flex-shrink-0"
            >
              Set Up →
            </button>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'This Week', value: `$${(earnings?.thisWeek || 0).toFixed(2)}` },
            { label: 'This Month', value: `$${(earnings?.thisMonth || 0).toFixed(2)}` },
            { label: 'All Time', value: `$${(earnings?.totalEarned || 0).toFixed(2)}` },
          ].map((e) => (
            <div key={e.label} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 text-center">
              <div className="text-xl font-bold text-yellow-400">{e.value}</div>
              <div className="text-xs text-gray-500 mt-1">{e.label}</div>
            </div>
          ))}
        </div>

        <PreferredDriverPanel />

        {/* Active job */}
        {activeJob && (
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-yellow-400">Active Delivery</h2>
              <DeliveryStatusBadge status={activeJob.status} />
            </div>

            <div className="space-y-3 mb-5">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Pickup</p>
                <p className="text-white text-sm mt-0.5">{activeJob.pickup?.address}</p>
                {activeJob.pickup?.notes && (
                  <p className="text-xs text-gray-400 mt-0.5">📝 {activeJob.pickup.notes}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Dropoff</p>
                <p className="text-white text-sm mt-0.5">{activeJob.dropoff?.address}</p>
                {activeJob.dropoff?.recipientName && (
                  <p className="text-xs text-gray-400 mt-0.5">👤 {activeJob.dropoff.recipientName}</p>
                )}
                {activeJob.dropoff?.notes && (
                  <p className="text-xs text-gray-400 mt-0.5">📝 {activeJob.dropoff.notes}</p>
                )}
              </div>
              <div className="flex items-center gap-4 pt-2">
                <span className="text-yellow-400 font-semibold">${activeJob.driverGuaranteed} guaranteed</span>
                {activeJob.orderDetails?.orderNumber && (
                  <span className="text-xs text-gray-400">Order {activeJob.orderDetails.orderNumber}</span>
                )}
              </div>
            </div>

            {/* Action buttons based on status */}
            <div className="flex gap-3">
              {activeJob.status === 'assigned' && (
                <>
                  <button
                    onClick={() => handleJobStatusUpdate(activeJob._id, 'accepted')}
                    className="flex-1 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition"
                  >
                    ✓ Accept
                  </button>
                  <button
                    onClick={() => handleJobStatusUpdate(activeJob._id, 'cancelled')}
                    className="flex-1 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold rounded-xl transition"
                  >
                    ✗ Decline
                  </button>
                </>
              )}
              {activeJob.status === 'accepted' && (
                <button
                  onClick={() => handleJobStatusUpdate(activeJob._id, 'picked_up')}
                  className="flex-1 py-3 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-xl transition"
                >
                  📦 Mark Picked Up
                </button>
              )}
              {activeJob.status === 'picked_up' && (
                <button
                  onClick={() => handleJobStatusUpdate(activeJob._id, 'delivered')}
                  className="flex-1 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition"
                >
                  🎯 Mark Delivered
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recent jobs */}
        <div>
          <h2 className="font-semibold mb-3">Recent Jobs</h2>
          {jobs.filter((j) => !['assigned', 'accepted', 'picked_up'].includes(j.status)).length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center text-gray-500">
              <p className="text-3xl mb-2">🚗</p>
              <p className="text-sm">No completed jobs yet</p>
              <p className="text-xs mt-1">Set yourself to Available to start receiving jobs</p>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
              {jobs
                .filter((j) => !['assigned', 'accepted', 'picked_up'].includes(j.status))
                .slice(0, 10)
                .map((job, i) => (
                  <div
                    key={job._id}
                    className={`flex items-center justify-between p-4 ${i > 0 ? 'border-t border-gray-800' : ''}`}
                  >
                    <div>
                      <p className="text-sm text-white truncate max-w-xs">{job.dropoff?.address}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-yellow-400">${job.driverGuaranteed}</span>
                      <DeliveryStatusBadge status={job.status} />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </main>
      {ratingDelivery && (
        <RatingModal
          delivery={ratingDelivery}
          userRole="driver"
          token={localStorage.getItem('starfuel_token')}
          onClose={() => setRatingDelivery(null)}
          onSubmit={() => setRatingDelivery(null)}
        />
      )}
    </div>
  );
}
